import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { calculateTooltipStyle, findTutorialTarget, getTutorialInteractiveTargetIds } from "./tutorialEngine";

export default function TutorialOverlay({
  open,
  tutorialId = null,
  notice = null,
  step,
  spotlightRect,
  guideSpotlightRects = [],
  stepIndex,
  stepCount,
  canGoBack,
  onPrev,
  onNext,
  onExit,
  confirmationVisible = false,
  onConfirm,
  onCancel,
}) {
  const bridgeForwardingRef = useRef(false);
  useEffect(() => {
    if (!open && !notice) return undefined;
    function onKeyDown(event) {
      if (event.key === "Escape") onExit();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [notice, open, onExit]);

  useEffect(() => {
    if (!open && !notice) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [notice, open]);

  useEffect(() => {
    if (!open && !notice) return undefined;
    const chromeSelectors = [
      '[data-testid="environment-badge"]',
      '[data-testid="build-mismatch-banner"]',
      '[data-testid="build-stamp"]',
      '[data-testid="dev-role-viewer"]',
    ];
    const previousZIndexes = chromeSelectors
      .flatMap((selector) => Array.from(document.querySelectorAll(selector)))
      .map((element) => [element, element.style.zIndex]);

    previousZIndexes.forEach(([element]) => {
      element.style.zIndex = "1100";
    });

    return () => {
      previousZIndexes.forEach(([element, zIndex]) => {
        element.style.zIndex = zIndex;
      });
    };
  }, [notice, open]);

  const tooltipStyle = useMemo(() => {
    if (!spotlightRect) return null;
    const avoidRects = guideSpotlightRects.length > 0
      ? guideSpotlightRects
      : step?.hideSpotlight ? [] : [spotlightRect];
    return calculateTooltipStyle(spotlightRect, step?.tooltipPosition || "auto", undefined, { avoidRects });
  }, [guideSpotlightRects, spotlightRect, step?.hideSpotlight, step?.tooltipPosition]);

  if (notice) {
    const noticeOverlay = (
      <div data-testid="tutorial-notice-overlay" className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/65 p-4">
        <section className="w-full max-w-md rounded-lg border border-teal-300/20 bg-[#0d1f1e] p-5 text-white shadow-2xl">
          <p className="text-[15px] leading-6 text-teal-50">{notice.message}</p>
          {notice.kind === "complete" ? (
            <button
              type="button"
              data-testid="tutorial-completion-done"
              onClick={onExit}
              className="mt-4 min-h-11 rounded-md bg-[#5eead4] px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-[#99f6e4]"
            >
              Done
            </button>
          ) : null}
        </section>
      </div>
    );
    return createPortal(noticeOverlay, document.body);
  }

  if (!open || !step || !spotlightRect || !tooltipStyle) return null;
  const interactiveTargetIds = getTutorialInteractiveTargetIds(step);
  const hasInteractiveTarget = interactiveTargetIds.length > 0;
  const isTypedGate = Number(step.typedMinLength) > 0 && typeof step.triggerEvent === "string" && step.triggerEvent.startsWith("tutorial:");
  const hideSpotlight = !!step.hideSpotlight;
  const shouldBlockSpotlightClicks = !hideSpotlight && !hasInteractiveTarget && !step.allowSpotlightInteraction;
  const shouldBridgeSpotlightClicks = !hideSpotlight
    && isSoftGuideTutorial(tutorialId)
    && hasInteractiveTarget
    && step.hideNext
    && !isTypedGate
    && (!Array.isArray(step.completionTargetIds) || step.completionTargetIds.length === 0);
  const spotlightShape = ["pill", "circle"].includes(spotlightRect.shape) ? spotlightRect.shape : "rect";
  const spotlightRadiusClass = spotlightShape === "rect" ? "rounded-lg" : "rounded-full";
  const isGuideTutorial = isSoftGuideTutorial(tutorialId);
  const spotlightTone = isGuideTutorial ? "guide" : "focus";
  const spotlightClass = spotlightTone === "guide"
    ? `fixed ${spotlightRadiusClass} border-2 border-[#5eead4] bg-[#5eead4]/5 shadow-[0_0_0_1px_rgba(94,234,212,0.28),0_0_18px_rgba(94,234,212,0.5)] transition-all duration-200 ease-out pointer-events-none`
    : `fixed ${spotlightRadiusClass} border-4 border-[#5eead4] shadow-[0_0_0_2px_rgba(94,234,212,0.35),0_0_28px_rgba(94,234,212,0.8)] transition-all duration-200 ease-out pointer-events-none`;
  const forwardSpotlightClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (bridgeForwardingRef.current) return;
    bridgeForwardingRef.current = true;
    const delayMs = step.advanceDelayMs ?? 300;
    const target = interactiveTargetIds.map((targetId) => findTutorialTarget(targetId)).find(Boolean);
    if (target && typeof target.click === "function") {
      window.setTimeout(() => target.click(), 0);
    }
    window.setTimeout(() => {
      onNext?.();
      bridgeForwardingRef.current = false;
    }, delayMs);
  };

  const overlay = (
    <div data-testid="tutorial-overlay" className="fixed inset-0 z-[1200] pointer-events-none">
      {hideSpotlight ? (
        <div className="fixed inset-0 bg-black/65 transition-all duration-200 ease-out pointer-events-auto" />
      ) : (
        <Mask rect={spotlightRect} tone={spotlightTone} />
      )}
      {!hideSpotlight && guideSpotlightRects.map((rect, index) => {
        const guideShape = ["pill", "circle"].includes(rect.shape) ? rect.shape : "rect";
        const radiusClass = guideShape === "rect" ? "rounded-lg" : "rounded-full";
        return (
          <div
            key={`${rect.left}-${rect.top}-${index}`}
            data-testid="tutorial-guide-spotlight"
            data-spotlight-shape={guideShape}
            className={`fixed ${radiusClass} border-2 border-[#5eead4]/80 bg-[#5eead4]/10 shadow-[0_0_18px_rgba(94,234,212,0.55)] transition-all duration-200 ease-out pointer-events-none`}
            style={{
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
            }}
          />
        );
      })}
      {!hideSpotlight ? (
        <div
          data-testid="tutorial-spotlight"
          data-spotlight-shape={spotlightShape}
          className={spotlightClass}
          style={{
            top: spotlightRect.top,
            left: spotlightRect.left,
            width: spotlightRect.width,
            height: spotlightRect.height,
          }}
        />
      ) : null}
      {!hideSpotlight && isGuideTutorial && hasInteractiveTarget ? (
        <div
          data-testid="tutorial-action-chip"
          className="tutorial-action-chip-bounce fixed z-[1201] rounded-full border border-teal-200/70 bg-[#ecfeff] px-3 py-1 text-[12px] font-semibold text-teal-950 shadow-[0_8px_22px_rgba(15,23,42,0.22)] pointer-events-none"
          style={{
            top: Math.max(12, spotlightRect.top - 34),
            left: Math.min(Math.max(12, spotlightRect.left), Math.max(12, window.innerWidth - 140)),
          }}
        >
          Press here next
        </div>
      ) : null}
      {shouldBlockSpotlightClicks ? (
        <div
          data-testid="tutorial-spotlight-click-blocker"
          className={`fixed ${spotlightRadiusClass} pointer-events-auto bg-transparent`}
          style={{
            top: spotlightRect.top,
            left: spotlightRect.left,
            width: spotlightRect.width,
            height: spotlightRect.height,
          }}
          aria-hidden="true"
        />
      ) : null}
      {shouldBridgeSpotlightClicks ? (
        <button
          key={`bridge-${step.id}`}
          type="button"
          data-testid="tutorial-spotlight-action-bridge"
          className={`fixed z-[1202] ${spotlightRadiusClass} pointer-events-auto cursor-pointer bg-transparent p-0`}
          style={{
            top: spotlightRect.top,
            left: spotlightRect.left,
            width: spotlightRect.width,
            height: spotlightRect.height,
          }}
          aria-label="Press highlighted tutorial control"
          onClick={forwardSpotlightClick}
        />
      ) : null}
      {!hideSpotlight && step.showCursor ? <TutorialCursor rect={spotlightRect} /> : null}
      <section
        key={step.id}
        data-testid="tutorial-card"
        data-placement={tooltipStyle.placement}
        className="fixed rounded-lg border border-teal-300/20 bg-[#0d1f1e] p-4 text-white shadow-2xl transition-opacity duration-150 ease-out pointer-events-auto"
        style={{
          top: tooltipStyle.top,
          left: tooltipStyle.left,
          width: tooltipStyle.width,
          maxHeight: tooltipStyle.height,
          overflowY: "auto",
          paddingBottom: "calc(1rem + env(safe-area-inset-bottom))",
          animation: "tutorial-card-fade 160ms ease-out",
        }}
        aria-live="polite"
      >
        <div className="mb-2 flex items-start justify-between gap-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#5eead4]">
            {stepIndex + 1} of {stepCount}
          </p>
          <button
            type="button"
            data-testid="tutorial-exit"
            onClick={onExit}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-200 hover:bg-white/10 hover:text-white"
            aria-label="Exit tutorial"
          >
            <X className="h-4 w-4" strokeWidth={2.25} />
          </button>
        </div>
        <h2 className="text-lg font-semibold leading-snug">{step.heading}</h2>
        {step.progressLabel ? (
          <p
            data-testid="tutorial-progress-label"
            className="mt-2 inline-flex rounded-full border border-[#5eead4]/35 bg-[#5eead4]/12 px-2.5 py-1 text-[12px] font-semibold uppercase tracking-wide text-[#99f6e4]"
          >
            {step.progressLabel}
          </p>
        ) : null}
        <p className="mt-2 text-[15px] leading-6 text-teal-50/85">{step.body}</p>
        {hasInteractiveTarget ? (
          <p data-testid="tutorial-interaction-hint" className="mt-3 text-[13px] font-semibold text-[#5eead4]">
            {isTypedGate
              ? (step.typedGateUnlocked ? "Click Next to continue." : `Type at least ${step.typedMinLength} characters to show Next.`)
              : (isGuideTutorial ? "Press the highlighted control to continue." : "Tap the highlighted item to continue.")}
          </p>
        ) : null}
        {confirmationVisible && step.confirmation ? (
          <div className="mt-4 rounded-lg border border-teal-300/30 bg-white/8 p-3">
            <p className="text-[15px] leading-6 text-teal-50">{step.confirmation.body}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                data-testid="tutorial-confirm"
                onClick={onConfirm}
                className="min-h-11 rounded-md bg-[#5eead4] px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-[#99f6e4]"
              >
                Confirm
              </button>
              <button
                type="button"
                data-testid="tutorial-cancel"
                onClick={onCancel}
                className="min-h-11 rounded-md border border-teal-50/25 px-4 py-2 text-sm font-semibold text-teal-50 hover:bg-white/10"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}
        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            data-testid="tutorial-back"
            onClick={onPrev}
            disabled={!canGoBack}
            className="min-h-11 rounded-md px-3 py-2 text-sm font-semibold text-teal-50 hover:bg-white/10 disabled:cursor-not-allowed disabled:text-teal-50/35 disabled:hover:bg-transparent"
          >
            ← Back
          </button>
          {!step.hideNext ? (
            <button
              type="button"
              data-testid="tutorial-next"
              onClick={onNext}
              className="min-h-11 rounded-md bg-[#5eead4] px-3 py-2 text-sm font-semibold text-slate-950 hover:bg-[#99f6e4]"
            >
              {stepIndex + 1 >= stepCount ? "Finish" : "Next →"}
            </button>
          ) : null}
        </div>
      </section>
    </div>
  );

  return createPortal(overlay, document.body);
}

function isSoftGuideTutorial(tutorialId) {
  if (typeof tutorialId !== "string") return false;
  return tutorialId.startsWith("checkout-")
    || tutorialId === "front-desk-routine-checkout"
    || tutorialId === "accepted-not-paid-today"
    || tutorialId === "doctor-handoff-front-desk"
    || tutorialId === "patient-overview"
    || tutorialId === "recall-flow";
}

function TutorialCursor({ rect }) {
  const endLeft = rect.left + Math.min(rect.width * 0.62, rect.width - 12);
  const endTop = rect.top + Math.min(rect.height * 0.62, rect.height - 12);
  return (
    <div
      data-testid="tutorial-cursor"
      className="tutorial-cursor-takeover fixed z-[1201] h-7 w-7 text-[#5eead4] drop-shadow-[0_8px_14px_rgba(0,0,0,0.45)]"
      style={{
        "--tutorial-cursor-start-x": `${Math.max(12, endLeft - 140)}px`,
        "--tutorial-cursor-start-y": `${Math.max(12, endTop - 90)}px`,
        "--tutorial-cursor-end-x": `${endLeft}px`,
        "--tutorial-cursor-end-y": `${endTop}px`,
      }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 28 28" fill="none" className="h-full w-full">
        <path d="M5 3.5L22.5 14.2L14.8 15.7L11.2 23.5L5 3.5Z" fill="currentColor" stroke="#042f2e" strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function Mask({ rect, tone = "focus" }) {
  const seamBuffer = 1;
  const top = Math.max(0, rect.top - seamBuffer);
  const left = Math.max(0, rect.left - seamBuffer);
  const rightStart = rect.right + seamBuffer;
  const right = Math.max(0, window.innerWidth - rightStart);
  const bottom = Math.max(0, window.innerHeight - rect.bottom - seamBuffer);
  const middleTop = rect.top;
  const middleHeight = rect.height;
  const maskClass = tone === "guide"
    ? "bg-transparent"
    : "bg-black/65";
  return (
    <>
      <div data-testid="tutorial-mask-pane" className={`fixed left-0 right-0 top-0 ${maskClass} transition-all duration-200 ease-out pointer-events-auto`} style={{ height: top }} />
      <div data-testid="tutorial-mask-pane" className={`fixed left-0 ${maskClass} transition-all duration-200 ease-out pointer-events-auto`} style={{ top: middleTop, width: left, height: middleHeight }} />
      <div data-testid="tutorial-mask-pane" className={`fixed ${maskClass} transition-all duration-200 ease-out pointer-events-auto`} style={{ top: middleTop, left: rightStart, width: right, height: middleHeight }} />
      <div data-testid="tutorial-mask-pane" className={`fixed bottom-0 left-0 right-0 ${maskClass} transition-all duration-200 ease-out pointer-events-auto`} style={{ height: bottom }} />
    </>
  );
}
